/**
 * The two reads behind a room's rent-transfer destination (COL-533).
 *
 * Exactly what V1's Dashboard runs, in the same tenant session: a
 * `room_payment_config` row for an admin's explicit per-room override, and
 * `get_room_rent_account` for the resolved rent-receipt account. Both are
 * readable by the tenant whose room it is — V1 has been serving tenants their
 * IBAN through them all along.
 *
 * This is the hook V2's payments card should have used from the start. It
 * read `VITE_COLIVIQ_IBAN` instead, which is unset in dev, so the card showed
 * "Bank details on request" where V1 showed a real IBAN.
 */
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/lib/supabase";
import type {
  RoomPaymentConfig,
  RoomRentAccount,
} from "@/lib/rent-bank-transfer";

export type RoomRentAccountState = {
  paymentConfig: RoomPaymentConfig;
  rentAccount: RoomRentAccount;
  /** Neither answer has arrived yet. */
  isPending: boolean;
  /** Either read failed — NOT the same as "no account configured". */
  isError: boolean;
};

export function useRoomRentAccount(roomId: string | null | undefined) {
  const config = useQuery<RoomPaymentConfig>({
    queryKey: ["room-payment-config", roomId],
    enabled: !!roomId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("room_payment_config")
        .select("*")
        .eq("room_id", roomId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as RoomPaymentConfig;
    },
  });

  const account = useQuery<RoomRentAccount>({
    queryKey: ["room-rent-account", roomId],
    enabled: !!roomId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc(
        "get_room_rent_account",
        { p_room_id: roomId }
      );
      if (error) throw error;
      const row = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
      return row as RoomRentAccount;
    },
  });

  return {
    paymentConfig: config.data ?? null,
    rentAccount: account.data ?? null,
    /*
      A disabled query (no room id) sits at `pending` forever in TanStack v5,
      so it is only pending when there is actually a room to resolve --
      otherwise the card would load indefinitely for a booking with no room.
    */
    isPending: !!roomId && (config.isPending || account.isPending),
    isError: config.isError || account.isError,
  } satisfies RoomRentAccountState;
}
